import { useEffect, useRef, useState, useCallback, forwardRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCalendarDays, faChevronLeft, faChevronRight, faWandMagicSparkles, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import useUpgrade from '../hooks/useUpgrade.js';
import useAppBindings from '../hooks/useAppBindings.js';
import FirstBotPrompt from '../components/FirstBotPrompt.js';
import { getBestKnowledgePlan } from '../components/knowledgeUpgradeModal.js';

import AgentStudio from '../components/agentStudio/AgentStudio';
import LiveChatSettings from '../components/liveChatSettings.js';
import KnowledgeOverview from '../components/knowledgeOverview.js';
import ProviderHub from '../components/ProviderHub.js';
import NotificationCenter from '../components/notificationCenter.js';
import ProductUpdatesSetting from '../components/ProductUpdatesSetting.js';
import WidgetCustomizationTab from '../components/widgetCustomizationTab.js';
import Insights from '../components/Insights.js';
import ChatHistories from '../components/ChatHistories.js';
import Leads from '../components/leads.js';
import Reporting from '../components/reporting.js';
import WarmLeadConfig from '../components/warmLeadConfig.js';

import './botSettings.css';
import './analyticsSuite.css';
import './sectionPages.css';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function SectionSpinner() {
  return (
    <div className="section-loading">
      <FontAwesomeIcon icon={faSpinner} spin />
    </div>
  );
}

// Resolves the global bot context into a render-ready state, returning a
// `gate` node (spinner / first-bot prompt) when the page can't render yet.
function useBotGate() {
  const ctx = useSelectedBot();
  let gate = null;
  if (ctx.loading) {
    gate = <SectionSpinner />;
  } else if (!ctx.bots.length) {
    gate = <FirstBotPrompt onAddBot={ctx.openAddBot} />;
  } else if (!ctx.selectedBotId) {
    gate = <SectionSpinner />;
  }
  return { ...ctx, gate };
}

// Re-creates the per-tab unsaved-changes guard that the old Bot Settings
// shell wired up, but scoped to a single standalone section page. The
// child component reports its dirty state and exposes save / discard
// handlers; navigating away while dirty surfaces the shared modal.
function GuardedSection({ guardId, title, message, children }) {
  const { registerUnsavedChangesGuard } = useUnsavedChanges();
  const [dirty, setDirty] = useState(false);
  const handlersRef = useRef({});

  useEffect(
    () =>
      registerUnsavedChangesGuard(guardId, {
        when: dirty,
        onSave: () => handlersRef.current.save?.(),
        onDiscard: () => handlersRef.current.discard?.(),
        title,
        message,
      }),
    [dirty, guardId, title, message, registerUnsavedChangesGuard]
  );

  return children({
    onUnsavedStateChange: setDirty,
    onUnsavedHandlersChange: (h) => {
      handlersRef.current = h || {};
    },
  });
}

// 'YYYY-MM-DD' string ↔ local Date (avoid the UTC shift of `new Date(str)`).
function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function toYMD(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
const dayMs = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const DateRangeTrigger = forwardRef(({ label, onClick }, ref) => {
  const { t } = useTranslation();
  return (
    <button type="button" className="date-range-trigger" onClick={onClick} ref={ref}>
      <FontAwesomeIcon icon={faCalendarDays} className="date-range-trigger-icon" />
      <span>{label || t('select_date_range')}</span>
    </button>
  );
});

function DateRangeBar({ startDate, endDate, setStartDate, setEndDate }) {
  const { t } = useTranslation();
  // Local range so the in-progress selection (start picked, end pending) shows
  // in the calendar WITHOUT pushing a half range to the data fetch — a missing
  // end would hit the API as `end_date=null` and 500.
  const [range, setRange] = useState(() => [parseYMD(startDate), parseYMD(endDate)]);
  // First-clicked day of an in-progress range — a ref, so hovering never
  // re-renders the picker. We order the two clicks ourselves, so a second
  // click BEFORE the first works too (auto-swap). We drive the whole range
  // selection by hand (no `selectsRange`) to avoid react-datepicker's
  // forward-only preview and its reset/auto-close flicker on a backward pick.
  const anchorRef = useRef(null);
  const dpRef = useRef(null);
  const calRef = useRef(null);

  useEffect(() => {
    setRange([parseYMD(startDate), parseYMD(endDate)]);
    anchorRef.current = null;
  }, [startDate, endDate]);

  // The live preview is painted straight onto the day DOM nodes — not through
  // React state — so it's instant (no re-render per mouse move) and can't
  // trigger the close/re-open flicker a state update was causing.
  const clearPreview = useCallback(() => {
    const cal = calRef.current;
    if (!cal) return;
    cal
      .querySelectorAll('.ub-preview-edge, .ub-preview-mid')
      .forEach((el) => el.classList.remove('ub-preview-edge', 'ub-preview-mid'));
  }, []);

  const paintPreview = useCallback((hv) => {
    const cal = calRef.current;
    const anchor = anchorRef.current;
    if (!cal || anchor == null) return;
    const av = dayMs(anchor);
    const lo = Math.min(av, hv);
    const hi = Math.max(av, hv);
    cal.querySelectorAll('.react-datepicker__day').forEach((el) => {
      el.classList.remove('ub-preview-edge', 'ub-preview-mid');
      const m = el.className.match(/ub-d-(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return;
      const dv = new Date(+m[1], +m[2] - 1, +m[3]).getTime();
      if (dv === av || dv === hv) el.classList.add('ub-preview-edge');
      else if (dv > lo && dv < hi) el.classList.add('ub-preview-mid');
    });
  }, []);

  const onCalOver = useCallback(
    (e) => {
      if (anchorRef.current == null) return;
      const dayEl = e.target.closest ? e.target.closest('.react-datepicker__day') : null;
      if (!dayEl) return;
      const m = dayEl.className.match(/ub-d-(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return;
      paintPreview(new Date(+m[1], +m[2] - 1, +m[3]).getTime());
    },
    [paintPreview]
  );

  const handleCalOpen = () => {
    requestAnimationFrame(() => {
      const cal = document.querySelector('.ub-datepicker');
      if (!cal) return;
      calRef.current = cal;
      cal.addEventListener('mouseover', onCalOver);
      cal.addEventListener('mouseleave', clearPreview);
    });
  };

  const handleCalClose = () => {
    const cal = calRef.current;
    if (cal) {
      cal.removeEventListener('mouseover', onCalOver);
      cal.removeEventListener('mouseleave', clearPreview);
    }
    clearPreview();
    calRef.current = null;
    anchorRef.current = null;
    // Discard an incomplete selection (revert to the committed range).
    setRange((cur) => (cur[0] && cur[1] ? cur : [parseYMD(startDate), parseYMD(endDate)]));
  };

  const handleDayClick = (date) => {
    if (!date) return;
    if (anchorRef.current == null) {
      // First click — set the anchor; keep the calendar open, commit nothing.
      anchorRef.current = date;
      setRange([date, null]);
    } else {
      // Second click — order the endpoints (works both directions) and commit.
      const a = anchorRef.current;
      const start = date < a ? date : a;
      const end = date < a ? a : date;
      anchorRef.current = null;
      clearPreview();
      setRange([start, end]);
      setStartDate(toYMD(start));
      setEndDate(toYMD(end));
      dpRef.current?.setOpen(false);
    }
  };

  const fmtLabel = (ymd) => {
    const d = parseYMD(ymd);
    return d
      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
  };
  const label = startDate && endDate ? `${fmtLabel(startDate)} - ${fmtLabel(endDate)}` : '';

  return (
    <div className="date-range-bar">
      <ReactDatePicker
        ref={dpRef}
        selected={null}
        onChange={handleDayClick}
        onCalendarOpen={handleCalOpen}
        onCalendarClose={handleCalClose}
        shouldCloseOnSelect={false}
        openToDate={range[0] || undefined}
        monthsShown={2}
        renderCustomHeader={({ monthDate, customHeaderCount, decreaseMonth, increaseMonth }) => (
          <div className="ub-dp-header">
            <button
              type="button"
              className="ub-dp-nav"
              onClick={decreaseMonth}
              aria-label={t('datepicker_prev_month')}
              style={{ visibility: customHeaderCount === 0 ? 'visible' : 'hidden' }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="ub-dp-month">
              {monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              className="ub-dp-nav"
              onClick={increaseMonth}
              aria-label={t('datepicker_next_month')}
              style={{ visibility: customHeaderCount === 1 ? 'visible' : 'hidden' }}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
        dateFormat="MMM d, yyyy"
        placeholderText={t('select_date_range')}
        dayClassName={(date) => {
          // Encode the date in a class so the imperative hover handler can map a
          // hovered DOM node back to its day without re-rendering.
          let cls = `ub-d-${toYMD(date)}`;
          const [s, e] = range;
          const dv = dayMs(date);
          if (s && e) {
            const sv = dayMs(s);
            const ev = dayMs(e);
            if (dv === sv || dv === ev) cls += ' ub-day-edge';
            else if (dv > sv && dv < ev) cls += ' ub-day-mid';
          } else if (s && dv === dayMs(s)) {
            cls += ' ub-day-edge';
          }
          return cls;
        }}
        customInput={<DateRangeTrigger label={label} />}
        calendarClassName="ub-datepicker"
        popperClassName="ub-datepicker-popper"
        popperPlacement="bottom-end"
        portalId="ub-datepicker-portal"
        showPopperArrow={false}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent Studio
// ---------------------------------------------------------------------------

// "AI Studio", the start page: ask Mo about the agent and the account, build the
// bot's customer agent in conversation, test it as a customer and see for every
// answer what really happened. See src/components/agentStudio/AgentStudio.js.
export function SkillsPage() {
  const { t } = useTranslation();
  const { gate, selectedBotId, loading, bots, openAddBot } = useBotGate();
  if (!loading && !bots.length) {
    return <FirstBotPrompt onAddBot={openAddBot} ctaLabel={t('agent_studio_build_new_agent')} />;
  }
  if (gate) return gate;
  return <AgentStudio key={`agent-studio-${selectedBotId}`} botId={selectedBotId} />;
}

// Behaviour moved into the Studio (you now shape tone/behaviour by instructing the
// agent, alongside skills). The old page is kept so links and muscle memory don't
// 404: a blurred STATIC mock (no live component → no extra fetch/WebSocket) behind
// an overlay that points to the Studio.
export function BehaviorPage() {
  const { gate } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell behavior-moved">
      <div className="behavior-moved-wrap">
        <div className="behavior-moved-blur" aria-hidden="true">
          {/* Header + skeleton are purely decorative here (the blur means they
              are never readable); the real heading lives in the overlay. */}
          <header className="section-head">
            <h1 className="section-head-title">{t('tone_and_behaviour')}</h1>
            <p className="section-head-sub">{t('behavior_moved_sub')}</p>
          </header>
          <div className="behavior-moved-mock">
            {[0, 1, 2].map((i) => (
              <div className="behavior-moved-card" key={i}>
                <div className="behavior-moved-line lg" />
                <div className="behavior-moved-line" />
                <div className="behavior-moved-line sm" />
              </div>
            ))}
          </div>
        </div>
        <div className="behavior-moved-overlay">
          <span className="behavior-moved-badge"><FontAwesomeIcon icon={faWandMagicSparkles} /> {t('behavior_moved_badge')}</span>
          <h2>{t('behavior_moved_title')}</h2>
          <p>{t('behavior_moved_body')}</p>
          <Link className="behavior-moved-cta" to="/agent-studio/skills">
            {t('behavior_moved_cta')} <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Warm-lead settings retired: lead capture is now the "Lead Generation" skill,
// managed by instructing the agent in the Studio (recipients/channels still live
// in the Notification Center). The old config page is shown blurred + inert
// behind a "moved to Studio" overlay so links and muscle memory don't 404.
// Unlike the behaviour page (a static mock), this renders the REAL old config —
// the user asked to show it — but the blur wrapper is pointer-events:none +
// aria-hidden, so it can't be edited and its unsaved-changes guard stays inert.
export function LeadGenerationPage() {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell behavior-moved lead-gen-moved">
      <div className="behavior-moved-wrap">
        <div className="behavior-moved-blur" aria-hidden="true">
          <WarmLeadConfig key={`lead-gen-${selectedBotId}`} botId={selectedBotId} preview />
        </div>
        <div className="behavior-moved-overlay">
          <span className="behavior-moved-badge"><FontAwesomeIcon icon={faWandMagicSparkles} /> {t('lead_generation_moved_badge')}</span>
          <h2>{t('lead_generation_moved_title')}</h2>
          <p>{t('lead_generation_moved_body')}</p>
          <Link className="behavior-moved-cta" to="/agent-studio/skills">
            {t('lead_generation_moved_cta')} <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live
// ---------------------------------------------------------------------------

export function LiveSettingsPage() {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head">
        <h1 className="section-head-title">{t('live_settings_title')}</h1>
        <p className="section-head-sub">{t('live_settings_sub')}</p>
      </header>
      <GuardedSection
        guardId="live_settings"
        title={t('live_settings_unsaved_title')}
        message={t('live_settings_unsaved_msg')}
      >
        {(guardProps) => (
          <LiveChatSettings
            key={`live-settings-${selectedBotId}`}
            botId={selectedBotId}
            {...guardProps}
          />
        )}
      </GuardedSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge Base — one section per sidebar leaf
// ---------------------------------------------------------------------------

// Title + subtitle per Knowledge Base leaf, shown in place of the old in-page
// info boxes.
// `titleKey` / `subKey` are resolved via t() at the render site (never at module
// scope). Brand-name titles (Google Drive, OneDrive, Notion) stay English, so they
// carry a literal `title` instead of a key.
const KNOWLEDGE_HEAD = {
  links: {
    titleKey: 'kb_links_title',
    subKey: 'kb_links_sub',
  },
  documents: {
    titleKey: 'kb_documents_title',
    subKey: 'kb_documents_sub',
  },
  products: {
    titleKey: 'kb_products_title',
    subKey: 'kb_products_sub',
  },
  faq: {
    titleKey: 'kb_faq_title',
    subKey: 'kb_faq_sub',
  },
  business_context: {
    titleKey: 'kb_business_context_title',
    subKey: 'kb_business_context_sub',
  },
  googledrive: {
    title: 'Google Drive',
    subKey: 'kb_googledrive_sub',
  },
  onedrive: {
    title: 'OneDrive',
    subKey: 'kb_onedrive_sub',
  },
  notion: {
    title: 'Notion',
    subKey: 'kb_notion_sub',
  },
};

// Knowledge sources usage bar, shown top-right of every Knowledge Base section
// header. `usage` is reported by KnowledgeOverview via `onUsageData` and is null
// until the bot's knowledge has loaded, so nothing renders before then.
//
// At the limit the bar also carries an Upgrade CTA (same pattern as the Studio
// usage bar). `onUpgrade` is the SAME action as KnowledgeUpgradeModal's "Upgrade
// Now": useUpgrade → Wix / Shopify / billing page. Omitted at the top tier, where
// that modal offers Contact Sales instead.
// Also rendered by the agent-creation flow's Knowledge step (same widget, same numbers).
export function KnowledgeUsageBar({ usage, onUpgrade, upgradeLoading }) {
  const { t } = useTranslation();
  if (!usage) return null;
  const { knowledgeCount, knowledgeLimit, isAtLimit } = usage;
  const pct = knowledgeLimit > 0 ? Math.min((knowledgeCount / knowledgeLimit) * 100, 100) : 0;
  const withCta = isAtLimit && !!onUpgrade;
  return (
    <div className={`kb-usage${withCta ? ' kb-usage-cta' : ''}`} role="status">
      <span className="kb-usage-label">
        {t('kb_sources_used', {
          used: knowledgeCount.toLocaleString('de-CH'),
          limit: knowledgeLimit.toLocaleString('de-CH'),
        })}
      </span>
      <span className="kb-usage-track">
        <span
          className={`kb-usage-fill${isAtLimit ? ' kb-usage-fill-full' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="kb-usage-pct">{Math.round(pct)}%</span>
      {withCta && (
        <button
          type="button"
          className="kb-usage-upgrade"
          onClick={onUpgrade}
          disabled={upgradeLoading}
        >
          {upgradeLoading
            ? <FontAwesomeIcon icon={faSpinner} spin />
            : t('upgrade')}
        </button>
      )}
    </div>
  );
}

// Knowledge Base leaves backed by a connected platform app rather than by
// knowledge_data rows the owner uploads. Brand names stay English (glossary), so
// they carry a literal label + the provider's own mark.
const KNOWLEDGE_PROVIDERS = {
  wix: {
    label: 'Wix',
    logo: '/wix-logo.png',
    // Wordmark: constrain the height, let the width follow.
    logoStyle: { height: 18, width: 'auto' },
    subKey: 'kb_provider_wix_sub',
  },
  shopify: {
    label: 'Shopify',
    logo: '/shopify-logo.svg',
    logoStyle: { height: 26, width: 'auto' },
    subKey: 'kb_provider_shopify_sub',
  },
};

// A Knowledge Base section whose contents come from a connected platform app
// (Wix / Shopify) instead of the shared KnowledgeOverview host.
//
// Deliberately WITHOUT the KnowledgeUsageBar: the shared source quota counts
// knowledge_data rows the owner adds, and a mirrored catalog or a CMS collection
// is explicitly excluded from that count (knowledgeOverview.js) — showing the bar
// here would attach a number to a page none of whose data moves it.
//
// The route stays mounted for unbound agents (deep links, a disconnect while the
// page is open), so the not-connected case is handled here rather than falling
// through to the generic OAuth connect detail: neither app can be connected from
// the portal at all, so the honest answer is a pointer, not a connect button.
export function KnowledgeProviderPage({ provider }) {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const bindings = useAppBindings(selectedBotId);
  const meta = KNOWLEDGE_PROVIDERS[provider];
  // ?dimension=<key> drills straight into one area (the skill builder's
  // "View products", forwarded here by AppsPage).
  const initialDimension = searchParams.get('dimension') || null;
  if (gate) return gate;
  if (!meta) return null;
  const bound = !!bindings[provider];
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head">
        <div className="kb-provider-head">
          <span className="kb-provider-head-mark">
            <img src={meta.logo} alt="" style={meta.logoStyle} />
          </span>
          <div>
            <h1 className="section-head-title">{meta.label}</h1>
            <p className="section-head-sub">{t(meta.subKey)}</p>
          </div>
        </div>
      </header>
      {bound ? (
        <ProviderHub
          key={`hub-${provider}-${selectedBotId}`}
          provider={provider}
          botId={selectedBotId}
          initialDimension={initialDimension}
          variant="page"
        />
      ) : (
        <div className="kb-provider-unbound">
          <p>{t('kb_provider_unbound', { app: meta.label })}</p>
          <Link className="kb-provider-unbound-link" to="/agent-studio/apps">
            {t('kb_provider_unbound_cta')} <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      )}
    </div>
  );
}

export function KnowledgePage({ section }) {
  const { gate, selectedBotId, selectedBot } = useBotGate();
  const { t } = useTranslation();
  const [usage, setUsage] = useState(null);
  // Same upgrade entry point KnowledgeOverview hands to KnowledgeUpgradeModal —
  // the bar's CTA skips the modal and calls it directly. `selectedBot` is the
  // bots-list row (carries wix_instance_id / subscription / subscription_plan),
  // which is what handleUpgradeClick branches on.
  const { handleUpgradeClick, isUpgradeLoading } = useUpgrade();
  if (gate) return gate;
  const head = KNOWLEDGE_HEAD[section] || {};
  // No tier left to sell -> no CTA (the modal's Contact-Sales case).
  const canUpgrade = !!selectedBot && !!getBestKnowledgePlan(
    selectedBot?.subscription_plan?.name || 'Free',
    usage?.knowledgeCount ?? 0,
  );
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head section-head-row">
        <div>
          <h1 className="section-head-title">{head.titleKey ? t(head.titleKey) : head.title}</h1>
          <p className="section-head-sub">{head.subKey ? t(head.subKey) : head.sub}</p>
        </div>
        <KnowledgeUsageBar
          usage={usage}
          onUpgrade={canUpgrade ? () => handleUpgradeClick(selectedBot) : null}
          upgradeLoading={isUpgradeLoading(selectedBot?.id)}
        />
      </header>
      <KnowledgeOverview
        key={`knowledge-${selectedBotId}-${section}`}
        selectedBotId={selectedBotId}
        section={section}
        onUsageData={setUsage}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function NotificationsPage() {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head">
        <h1 className="section-head-title">{t('notifications')}</h1>
        <p className="section-head-sub">{t('notifications_page_sub')}</p>
      </header>
      <GuardedSection
        guardId="notifications"
        title={t('notifications_unsaved_title')}
        message={t('notifications_unsaved_msg')}
      >
        {(guardProps) => (
          <NotificationCenter
            key={`notifications-${selectedBotId}`}
            botId={selectedBotId}
            {...guardProps}
          />
        )}
      </GuardedSection>
      <ProductUpdatesSetting />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export function InsightsPage() {
  const { gate, bots, selectedBotIds, openAddBot } = useBotGate();
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head section-head-row">
        <div>
          <h1 className="section-head-title">{t('insights')}</h1>
          <p className="section-head-sub">{t('insights_page_sub')}</p>
        </div>
        <DateRangeBar
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </header>
      <Insights
        bots={bots}
        selectedBotIds={selectedBotIds}
        startDate={startDate}
        endDate={endDate}
        addBot={openAddBot}
      />
    </div>
  );
}

export function InboxPage() {
  const { gate, bots, selectedBotIds, openAddBot } = useBotGate();
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head section-head-row">
        <div>
          <h1 className="section-head-title">{t('inbox')}</h1>
          <p className="section-head-sub">{t('inbox_page_sub')}</p>
        </div>
        <DateRangeBar
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </header>
      <ChatHistories
        bots={bots}
        selectedBotIds={selectedBotIds}
        addBot={openAddBot}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />
    </div>
  );
}

export function LeadsPage({ setUnreadLeadsCount }) {
  const { gate, bots, selectedBotIds } = useBotGate();
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head section-head-row">
        <div>
          <h1 className="section-head-title">{t('leads')}</h1>
          <p className="section-head-sub">{t('leads_page_sub')}</p>
        </div>
        <DateRangeBar
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </header>
      <Leads
        bots={bots}
        selectedBotIds={selectedBotIds}
        setUnreadLeadsCount={setUnreadLeadsCount}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />
    </div>
  );
}

export function ReportingPage() {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head">
        <h1 className="section-head-title">{t('reporting')}</h1>
        <p className="section-head-sub">{t('reporting_page_sub')}</p>
      </header>
      <Reporting key={`reporting-${selectedBotId}`} botId={selectedBotId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deploy
// ---------------------------------------------------------------------------

export function DesignPage() {
  const { gate, selectedBotId } = useBotGate();
  const { t } = useTranslation();
  if (gate) return gate;
  return (
    <div className="bot-settings section-page section-shell">
      <header className="section-head">
        <h1 className="section-head-title">{t('design')}</h1>
        <p className="section-head-sub">{t('design_page_sub')}</p>
      </header>
      <GuardedSection
        guardId="design"
        title={t('design_unsaved_title')}
        message={t('design_unsaved_msg')}
      >
        {(guardProps) => (
          <WidgetCustomizationTab
            key={`design-${selectedBotId}`}
            botId={selectedBotId}
            {...guardProps}
          />
        )}
      </GuardedSection>
    </div>
  );
}
